/**
 * @file        src/components/PriceSummary.jsx
 * @description ONE shared flight fare-breakdown renderer, used everywhere a
 *              price breakdown is shown so the figures can never drift between
 *              screens (CR B3). Replaces ~5 hand-rolled implementations
 *              (FlightBook's `PriceDetails`, FlightReview's inline sidebar,
 *              FlightPayment's "Price Summary", Confirmation's wallet-split
 *              block, VoucherModal's flight `PriceRow`).
 *
 *              This component is DISPLAY ONLY — it never computes a figure.
 *              Every call site keeps its own local math (base fare, taxes,
 *              seat/baggage/meal totals, discounts, wallet split, grand
 *              total…) exactly as before and hands the shared component an
 *              already-resolved list of rows to render. `show`/`value` on
 *              each row is the SAME condition the call site used to gate its
 *              own inline `{cond && <div>...}` — moved, not changed.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-09-12 Claude — Both of the panel's collapsibles now open by DEFAULT —
 *                          the "Tickets (N)" fare split and the "Price breakdown
 *                          by passenger" section. Collapsed state is tracked as
 *                          `closedKeys` (inverted) so a row that renders later
 *                          still arrives expanded. Both toggles still work; the
 *                          rows, figures and totals are untouched. One responsive
 *                          panel serves both breakpoints, so this is the default
 *                          on mobile and desktop alike. Ported from Neon.
 *   - 2026-08-04 Claude — CR B3: new shared component.
 *   - 2026-08-04 Claude — CR B5: new optional `passengerBreakdown` prop — an
 *                          expandable "Price breakdown by passenger" section
 *                          (one block per passenger, their seat/meal picks +
 *                          a subtotal). Backward compatible: omitted/empty on
 *                          every existing call site (FlightReview/Payment/
 *                          Confirmation/Voucher), so only FlightBook renders
 *                          it. Same display-only rule as `rows` — every value
 *                          arrives already formatted.
 */
import { useState } from 'react';

/**
 * @typedef {Object} PriceSummaryRow
 * @property {string|number} [key] - React key; falls back to array index.
 * @property {import('react').ReactNode} label - left-hand text (base fare,
 *   taxes & fees, seat charges, meal charges, baggage charges, promo/coupon
 *   discount, insurance premium, convenience fee, processing fee,
 *   wallet-applied — one row per line item).
 * @property {import('react').ReactNode} value - already-formatted right-hand
 *   text (e.g. `inr(x)`, `'Free'`, `'—'`, `'− ' + inr(x)`).
 * @property {import('react').ReactNode} [icon] - optional glyph before the label.
 * @property {'default'|'accent'|'muted'} [tone='default'] - value text colour
 *   ('accent' = brand/neon, used for discounts and "Free"/zero fallbacks).
 * @property {string} [valueClassName] - escape hatch overriding `tone`
 *   entirely (e.g. Confirmation's plain `font-semibold` wallet-split values).
 * @property {string} [labelClassName='text-muted'] - escape hatch overriding
 *   the label span's class.
 * @property {boolean} [show=true] - whether to render the row at all. This is
 *   the same boolean each call site already computed inline
 *   (e.g. `processingFee > 0`) — the component does not decide it.
 * @property {boolean} [collapsible=false] - render as a toggle button over
 *   `subRows` (FlightBook's "Tickets (2 adults) ▾" row). Starts EXPANDED.
 * @property {PriceSummaryRow[]} [subRows] - shown when a collapsible row is open.
 */

/**
 * @typedef {Object} PriceSummaryTotal
 * @property {string} [label='Total']
 * @property {import('react').ReactNode} value
 * @property {string} [className] - row wrapper class override.
 * @property {string} [valueClassName='text-neon']
 * @property {string} [valueId] - optional id on the value span (DOM hook parity).
 */

/**
 * @typedef {Object} PassengerBreakdownItem
 * @property {string|number} [key]
 * @property {import('react').ReactNode} label - e.g. "Seat 14A (DEL → BOM)".
 * @property {import('react').ReactNode} value - already-formatted price, or "Free".
 */

/**
 * @typedef {Object} PassengerBreakdownEntry
 * @property {string|number} [key] - falls back to array index.
 * @property {import('react').ReactNode} label - passenger name ("Adult 1").
 * @property {import('react').ReactNode} subtotal - already-formatted per-passenger total.
 * @property {PassengerBreakdownItem[]} items - that passenger's seat/meal picks.
 */

function PassengerBreakdownPanel({ title, entries }) {
  // Open on arrival: the per-passenger split is part of reading the price, not
  // an optional extra, so the customer should not have to find a chevron to see
  // what each traveller is being charged. The toggle still collapses it.
  const [open, setOpen] = useState(true);
  if (!entries?.length) return null;
  return (
    <div className="mt-3 pt-3 border-t border-line">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-sm font-semibold text-ink cursor-pointer hover:text-neon transition"
      >
        <span>{title}</span>
        <svg viewBox="0 0 24 24" className={'w-4 h-4 shrink-0 transition-transform ' + (open ? 'rotate-180' : '')}>
          <path fill="currentColor" d="m7 10 5 5 5-5z" />
        </svg>
      </button>
      {open && (
        <div className="mt-2.5 space-y-2.5">
          {entries.map((p, i) => (
            <div key={p.key ?? i} className="rounded-lg border border-line bg-night/40 px-3 py-2.5">
              <div className="flex justify-between items-center text-sm font-semibold text-ink">
                <span>{p.label}</span>
                <span className="text-neon">{p.subtotal}</span>
              </div>
              {p.items?.length > 0 && (
                <div className="mt-1.5 space-y-1 text-xs">
                  {p.items.map((it, j) => (
                    <div className="flex justify-between text-muted" key={it.key ?? j}>
                      <span>{it.label}</span>
                      <span className="text-ink">{it.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ row, isOpen, onToggle }) {
  const toneClass = row.valueClassName
    || (row.tone === 'accent' ? 'text-neon' : row.tone === 'muted' ? 'text-muted' : 'text-ink');
  const labelClassName = row.labelClassName || 'text-muted';

  if (row.collapsible) {
    return (
      <div>
        <button
          type="button"
          onClick={onToggle}
          className="w-full flex justify-between items-center cursor-pointer hover:text-ink"
        >
          <span className={'inline-flex items-center gap-1 ' + labelClassName}>
            {row.label}
            <svg viewBox="0 0 24 24" className={'w-3.5 h-3.5 transition-transform ' + (isOpen ? 'rotate-180' : '')}>
              <path fill="currentColor" d="m7 10 5 5 5-5z" />
            </svg>
          </span>
          <span className="text-ink">{row.value}</span>
        </button>
        {isOpen && row.subRows?.length > 0 && (
          <div className="pl-3 space-y-1.5 border-l border-line ml-1">
            {row.subRows.map((sub, i) => (
              <div className="flex justify-between" key={sub.key ?? i}>
                <span className="text-muted">{sub.label}</span>
                <span className="text-ink">{sub.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex justify-between">
      <span className={(row.icon ? 'inline-flex items-center gap-1.5 ' : '') + labelClassName}>
        {row.icon}
        {row.label}
      </span>
      <span className={toneClass}>{row.value}</span>
    </div>
  );
}

function VoucherRow({ row }) {
  return (
    <div className="flex justify-between py-1 text-gray-600">
      <span>{row.label}</span>
      <span className="text-[#0A0F1E]">{row.value}</span>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {import('react').ReactNode} [props.title] - header text ("Price details" / "Price Summary").
 * @param {'h3'|'p'} [props.titleTag='h3']
 * @param {string} [props.titleClassName]
 * @param {string} [props.titleWrapperClassName] - wraps the title in a div with this class when set.
 * @param {PriceSummaryRow[]} props.rows
 * @param {string} [props.rowsClassName='space-y-2 text-sm']
 * @param {import('react').ReactNode} [props.footnote] - small note under the rows, above the divider.
 * @param {boolean} [props.showDivider=true] - render the `Total`-separating rule.
 * @param {PriceSummaryTotal} props.total
 * @param {'sidebar'|'voucher'} [props.variant='sidebar']
 * @param {PassengerBreakdownEntry[]} [props.passengerBreakdown] - optional
 *   collapsible "Price breakdown by passenger" section, rendered below `rows`
 *   (sidebar variant only) and EXPANDED on arrival. Omitted/empty renders
 *   nothing — every existing call site is unaffected.
 * @param {string} [props.passengerBreakdownTitle='Price breakdown by passenger']
 */
export default function PriceSummary({
  title,
  titleTag = 'h3',
  titleClassName,
  titleWrapperClassName,
  rows = [],
  rowsClassName = 'space-y-2 text-sm',
  footnote,
  showDivider = true,
  total,
  variant = 'sidebar',
  passengerBreakdown,
  passengerBreakdownTitle = 'Price breakdown by passenger',
}) {
  // Collapsible rows ("Tickets (2 adults)") start EXPANDED, so the fare's Base
  // fare / Taxes & fees / Processing fee split is on screen without a click.
  //
  // Tracked as the keys that are CLOSED rather than the keys that are open: a
  // seeded open-set would have to be built from `rows` at mount, and a row that
  // only appears on a later render (rows are rebuilt as the booking fills in)
  // would then default closed. Inverting the set makes "open" the state that
  // needs no registration, so every collapsible row is open whenever it arrives.
  const [closedKeys, setClosedKeys] = useState(() => new Set());
  const toggle = (key) => {
    setClosedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleRows = rows.filter((r) => r.show !== false);

  if (variant === 'voucher') {
    return (
      <div className="mt-1 text-sm">
        {visibleRows.map((row, i) => (
          <VoucherRow key={row.key ?? i} row={row} />
        ))}
        {total && (
          <div className="flex justify-between py-1 font-bold text-[#0A0F1E] border-t border-gray-200 mt-1 pt-2 text-base">
            <span>{total.label || 'Total'}</span>
            <span>{total.value}</span>
          </div>
        )}
      </div>
    );
  }

  const TitleTag = titleTag;
  const titleNode = title ? (
    <TitleTag className={titleClassName || (titleTag === 'p' ? 'font-semibold text-sm mb-1' : 'font-semibold mb-3')}>
      {title}
    </TitleTag>
  ) : null;

  return (
    <>
      {titleNode && (titleWrapperClassName ? <div className={titleWrapperClassName}>{titleNode}</div> : titleNode)}
      {visibleRows.length > 0 && (
        <div className={rowsClassName}>
          {visibleRows.map((row, i) => {
            const key = row.key ?? i;
            return <Row key={key} row={row} isOpen={!closedKeys.has(key)} onToggle={() => toggle(key)} />;
          })}
        </div>
      )}
      {footnote && <p className="text-[11px] text-muted mt-1">{footnote}</p>}
      <PassengerBreakdownPanel title={passengerBreakdownTitle} entries={passengerBreakdown} />
      {showDivider && <div className="h-px bg-line my-3"></div>}
      {total && (
        <div className={total.className || 'flex justify-between font-bold text-lg'}>
          <span>{total.label || 'Total'}</span>
          <span id={total.valueId} className={total.valueClassName || 'text-neon'}>{total.value}</span>
        </div>
      )}
    </>
  );
}
